import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
  type CanActivateFn,
} from '@angular/router';
import { Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';

import { getParamsObject } from '@cosmos/core';
import { LocalStorageService } from '@cosmos/data-access-storage';
import { getParam } from '@cosmos/router';
import { injectWindow } from '@cosmos/util-common/di';
import {
  createRetainAccessParams,
  hasRetainAccessQueryParam,
} from '@customer-portal/common/util-retain-access-code';
import { dispatchPdfDocumentError } from '@esp/common/util-pdf-generation';

import { CustomerPortalCollectionAuthActions } from '../actions';
import { CustomerPortalCollectionAuthQueries } from '../queries';
import { getCustomerPortalCollectionAuthDataFromLocalStorage } from '../utils/local-storage-auth-data';

export function customerPortalCollectionAccessGuard(): CanActivateFn {
  return async (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<UrlTree | boolean> => {
    const _store: Store = inject(Store);
    const _router: Router = inject(Router);
    const params = _getParams(route)!;
    const _lsService = inject(LocalStorageService);
    const _window = injectWindow();
    // Print routes (print or print/pdf) are also rendered by the PDF worker.
    const isPrintRoute = state.url.includes('/print');

    // A failed login means the page won't render — on a print route, abort the
    // PDF worker instead of letting it wait for the ready selector until it
    // times out.
    const login = async (action: object): Promise<void> => {
      try {
        await firstValueFrom(_store.dispatch(action), {
          defaultValue: undefined,
        });
      } catch (error) {
        if (isPrintRoute) dispatchPdfDocumentError(_window, error, 401);
        throw error;
      }
    };

    const session = _store.selectSnapshot(
      CustomerPortalCollectionAuthQueries.getSession
    );

    // Retention mode with a valid session: leave the URL untouched, otherwise
    // the engine's accessCode re-adds re-enter this guard and loop.
    if (session && hasRetainAccessQueryParam(route.queryParams)) {
      return true;
    }

    // The URL has no access code (either it was already stripped after a
    // previous login, or we're on a bookmarked/reloaded link), so we can't
    // tell here whether the stored token is still valid. Re-login with the
    // access code we already have on file rather than trusting it blindly.
    if (session && !params.accessCode) {
      const storedAccessCode = _store.selectSnapshot(
        CustomerPortalCollectionAuthQueries.getAccessCode
      );

      if (storedAccessCode) {
        await login(
          new CustomerPortalCollectionAuthActions.LoginWithAccessCode({
            accessCode: storedAccessCode,
            collectionId: params.collectionId!,
          })
        );
      }

      return true;
    }

    if (params.accessCode && params.collectionId) {
      await login(
        new CustomerPortalCollectionAuthActions.LoginWithAccessCode({
          accessCode: params.accessCode,
          collectionId: params.collectionId,
        })
      );

      // For print routes, don't redirect - let the resolver load the data
      // This prevents the page from being blank on initial load due to redirect
      if (isPrintRoute) {
        return true;
      }

      // Keep a marker and strip the raw accessCode; the engine re-applies it
      // from the store on marked routes.
      return _getUrlTreeWithRetainMarker(_router, state);
    }

    if (!session) {
      const collectionId = getParam(route, 'collectionId');

      if (collectionId) {
        const authData = getCustomerPortalCollectionAuthDataFromLocalStorage(
          _lsService,
          collectionId
        );
        if (authData) {
          await login(
            new CustomerPortalCollectionAuthActions.RestoreFromLocalStorage(
              authData.session,
              authData.accessCode
            )
          );
          return true;
        }
      }
    }

    if (isPrintRoute) dispatchPdfDocumentError(_window, null, 401);
    return _router.navigate(['/unauthorized'], { skipLocationChange: true });
  };
}

function _getParams(route: ActivatedRouteSnapshot) {
  const params = getParamsObject(route.paramMap, {
    optional: ['collectionId'],
  });

  const queryParams = getParamsObject(route.queryParamMap, {
    optional: ['accessCode'],
  });

  return (
    params &&
    queryParams && {
      // Keep the raw value — collection ids can be numeric or a UUID string.
      collectionId: params.collectionId,
      accessCode: queryParams.accessCode,
    }
  );
}

function _getUrlTreeWithRetainMarker(
  router: Router,
  state: RouterStateSnapshot
): UrlTree {
  const urlTree = router.parseUrl(state.url);
  delete urlTree.queryParams['accessCode'];
  Object.assign(urlTree.queryParams, createRetainAccessParams());
  return urlTree;
}
